import React from "react";
import A4PageFrame from "./A4PageFrame";
import ObjectVsImmeubleChart from "../../../components/charts/ObjectVsImmeubleChart";
import { fmtNum, fmtPct, fmtDate } from "./formatters";

// Phase 3 (M2) — "Résultat détaillé", one section per lease, rendered PORTRAIT.
//
// WHY PORTRAIT: the client's reference report renders this section portrait, with a
// whole object (all its cost accounts + Total/Acomptes/Solde + the
// Consommations block) on ONE page. Étang B has ~26 cost accounts; a portrait A4
// body fits ~47 rows (ROWS_PER_PAGE_PORTRAIT), so the whole object fits one page —
// whereas the short landscape body (~23 rows) split each object across 3 pages and
// tripled the report length. The cost + consommations tables use width:100% so they
// reflow to the portrait content width and do NOT clip on the right (verified against
// the reference portrait export — all 7 columns render).
//
// WHY PAGINATION SURVIVES: a rare object with more cost lines than fit one portrait
// page still splits via splitObjectDetail() — the object header + column header
// REPEAT on each page, and the Total/Acomptes/Solde + Consommations block
// render ONLY on the last page (never split/clipped). The Consommations block is
// configurable per building since 2026-09-01, so its height is derived from the actual
// row count (consoBlockRows) rather than assumed to be five rows.
// objectDetailPageCount() returns the page count so Preview.js's page-number +
// bookmark math agrees exactly.
//
// Consommations columns (client reference):
//     Consommations [energy + unit inline] | <year> | CHF/unité | Total Immeuble | Part %
// laid on the cost table's 7-column grid (client 2026-10-15) — see DETAIL_COL_WIDTHS.
// Rows come from the building's own configuration (tbl_energy_mapping). The client's
// 2026-09-01 default set is six: Chauffage kWh · Climatisation kWh · Eau m3 ·
// Eau chaude kWh · Eau chaude m3 · Electricité kWh — and any line can be switched off
// per project. Unconfigured buildings still get the legacy five.

// Row geometry, established 2026-07-21 ("reduce space between lines so all costs fit
// on 1 page") and unchanged since: padding 1 + lineHeight 12 + padding 1 + border 1
// = 15 px per row, inside a 936 px portrait content band (see pageMetrics.js).

// ── Block-aware page budget ─────────────────────────────────────────────────
// Client 2026-08-05: "the page is cutted in the middle of the cost list. Can we fix
// that the cut will be after the total of consumptions or after total of costs when
// there will be not enough place?"
//
// He is right, and the old model was the cause: it always reserved room for the
// WHOLE tail (Total + Consommations + graph) on the last page, then carved the cost
// list to fit. On Étang A (44 accounts) that forced a break after 14 rows even
// though the costs, the Total and the Consommations all fit one page together — only
// the graph did not.
//
// So budgets are expressed as COST ROWS a page can hold alongside a chosen set of
// tail blocks, and the splitter prefers breaking at a BLOCK boundary over cutting the
// cost list. Every budget derives from one per-variant no-tail number, so the tail
// costs (Total 1 row, Acomptes+Solde 2, Consommations 3 + one per configured line,
// graph 14) are subtracted consistently wherever they appear.
// PER-VARIANT, raised 2026-08-06. Client (Espace Tourbillon): "Résultat détaillé
// (module): fullfil 4 to 6 cost lines on page 1 to avoid empty space". He was right,
// and the cause was that a single shared budget was sized for the tallest header.
// Measured in Chrome at the old budget of 53 rows:
//   module variant (3-row header): 79 px free → 5 rows wasted
//   objet variant  (5-row header): 49 px free → 3 rows wasted
// The 30 px gap between them is exactly the two extra Info rows, which is why one
// shared number could not serve both. Each variant now gets its own budget, sized to
// its measured capacity minus a ~19 px (>1 row) cushion for sub-pixel rounding during
// html-to-image capture. The locataire header is 5 rows like objet, so it shares that
// budget.
const ROWS_PER_PAGE_NO_TAIL_BY_VARIANT = { object: 55, module: 57, tenant: 55 };
const ROWS_TOTAL_CHF = 1;         // the "Total CHF" line
const ROWS_ACOMPTES = 2;          // Acomptes + Solde, only when the toggle is on
// Chrome around the Consommations rows: top margin + the block's own header row +
// the column-header row. The DATA rows are counted separately because the block is no
// longer a fixed size — client 2026-09-01 made the lines configurable per building
// (six by default, including "Eau chaude" twice for kWh and m3, and each line can be
// switched off), so a hardcoded 8 would under-budget a 6-line building by a row and
// silently clip the bottom of a full cost page.
const ROWS_CONSO_CHROME = 3;
const LEGACY_CONSO_LINES = 5;     // the fixed set this budget was calibrated against
const consoBlockRows = (lineCount) =>
  ROWS_CONSO_CHROME + (Number.isFinite(lineCount) ? lineCount : LEGACY_CONSO_LINES);
const ROWS_CHART_BLOCK = 14;      // margin + border/padding + the 200px svg

// Cost rows that fit on one page next to the requested tail blocks.
// `consoLines` is the ACTUAL number of Consommations rows this entry will print.
// Defaulting it to LEGACY_CONSO_LINES keeps every existing call site's arithmetic
// identical to before this became configurable.
const costRowBudget = ({ totals = false, conso = false, chart = false, acomptes = false, variant = 'object', consoLines = LEGACY_CONSO_LINES }) => {
  let rows = ROWS_PER_PAGE_NO_TAIL_BY_VARIANT[variant] ?? ROWS_PER_PAGE_NO_TAIL_BY_VARIANT.object;
  if (totals) rows -= ROWS_TOTAL_CHF + (acomptes ? ROWS_ACOMPTES : 0);
  if (conso) rows -= consoBlockRows(consoLines);
  if (chart) rows -= ROWS_CHART_BLOCK;
  return Math.max(0, rows);
};

// ── Objet-vs-Immeuble chart (restored 2026-07-31, client request) ───────────
// Originally rendered on its OWN page and removed on 09.07.2026 because that page
// was pure overhead. The client asked for it back, but on the cost page: "The ideal
// is to have the graph on the same page of costs (only 1 page)". It therefore sits
// under the Consommations block, and only gets a page of its own when the entry is
// too tall to hold everything — see rule 2 in splitObjectDetail, which pushes the
// graph rather than cutting the cost list.
//
// Its height is ROWS_CHART_BLOCK rows: CHART_MARGIN + 1px border + 6px padding +
// CHART_H + 6px padding + 1px border ≈ 226 px ≈ 14 rows of 15 px.
const CHART_H = 200;
const CHART_MARGIN = 12;

// Client rule (28.07.2026) for the locataire header's Objets / Modules lines:
// "you can keep one line that will have the detail of 90% of cases and when there's
// more then 6 we have the number of objects or modules". So ≤ 6 entries are listed,
// and above that only the count. Applied independently to each of the two lines.
//
// `total` lets the Objets line report the true member count rather than the length
// of the de-duplicated list — a tenant can hold two leases on the same objet_code,
// and "86 objets" must mean 86 leases, not 85 distinct codes.
const LIST_LIMIT = 6;
// Separator matters here: objet codes CONTAIN a space ("710300.496 040.1"), so
// joining on whitespace makes it impossible to see where one code ends and the next
// begins. A middle dot is unambiguous and costs ~1 character per gap.
const LIST_SEP = " · ";
const listOrCount = (arr, total, noun) => {
  const list = arr || [];
  const n = total ?? list.length;
  if (!list.length) return "–";
  if (list.length <= LIST_LIMIT) return list.join(LIST_SEP);
  return `${n} ${noun}`;
};

// Does this entry have anything to plot? Both mixes all-zero (or absent) means no
// chart — same guard the original implementation used, so an entry with no energy
// costs doesn't render an empty axis.
const hasChartData = (d) => {
  const a = d?.object_mix || [];
  const b = d?.immeuble_mix || [];
  return a.some((e) => (e.pct || 0) > 0) || b.some((e) => (e.pct || 0) > 0);
};

// Merge object_mix + immeuble_mix into the {label, object, immeuble} rows the
// chart component expects, in the backend's canonical CHART_ORDER.
const toChartData = (d) => {
  const bld = new Map((d.immeuble_mix || []).map((e) => [e.energy, e.pct]));
  return (d.object_mix || []).map((e) => ({
    label: e.energy,
    object: e.pct || 0,
    immeuble: bld.get(e.energy) || 0,
  }));
};

// ── Phase 4 variants ────────────────────────────────────────────────────────
// Same table, same columns, same Consommations block — only the grouping and the
// header block differ. Titles carry the client's suffixes (his list, 28.07.2026):
// "Résultat détaillé (module)" / "(objet)" / "(locataire)".
//
// PAGINATION: every variant reuses the calibrated objet budgets, and that is safe
// because each header is at most FIVE Info rows of a FIXED 15 px:
//   objet     5 rows (Objet / Locataire / Module / Période / Surface)
//   module    3 rows (Module / Période / Surface) — 2 rows of spare space
//   locataire 5 rows (Locataire / Objets / Modules / Période / Surface)
// The locataire header was the one at risk, because its Objets/Modules lists could
// wrap to a second line and silently eat a cost row. Info() now pins every value to
// one line (nowrap + ellipsis), which is also exactly what the client asked for
// ("keep one line"), so the header height is constant and no variant needs its own
// budget. A conservative budget can waste space but never clip.
// `chartLabel` is the legend name for the entity's own bar. The building's bar is
// always "Immeuble". On a module page "Objet" would be wrong — the bar is the
// module's share — so each variant names itself.
const VARIANTS = {
  object: { title: 'Résultat détaillé (objet)', notIncluded: 'Résultat détaillé par objet — non inclus', noun: 'objets', chartLabel: 'Objet' },
  module: { title: 'Résultat détaillé (module)', notIncluded: 'Résultat détaillé par module — non inclus', noun: 'modules', chartLabel: 'Module' },
  tenant: { title: 'Résultat détaillé (locataire)', notIncluded: 'Résultat détaillé par locataire — non inclus', noun: 'locataires', chartLabel: 'Locataire' },
};

// Split one object's cost lines into page-sized chunks. Returns [{ lines, isLast }].
// The last chunk (isLast) carries the totals + consommations, so it reserves room for
// them. Guarantees ≥1 chunk (zero-cost object → one page). The last-page cost
// budget shrinks by 2 rows when acomptesEnabled=true because the Acomptes and
// Solde rows land on the last page too (see comment above).
// Split one entry into pages. Returns [{ lines, showTotals, showConso, showChart }].
//
// The rule the client asked for, in priority order — NEVER cut the cost list while a
// block boundary would do:
//   1. everything on one page;
//   2. else costs + Total + Consommations together, graph alone on page 2
//      ("the cut will be after the total of consumptions");
//   3. else costs + Total together, Consommations + graph on page 2
//      ("or after total of costs when there will be not enough place");
//   4. only when the cost list itself cannot fit one page does it split — and then
//      pages fill from the FRONT, so page 1 is full rather than nearly empty.
//
// `chart` is derived from the entry AND the building's Graphiques switch, not from a
// per-page prop, so the reservation made here and the render below can never disagree
// about whether a given entry needs chart space.
// `variant` selects the per-variant row budget (module pages hold more because their
// header is 2 rows shorter). Defaults to 'object' so existing 2-arg callers are
// unaffected.
//
// `graphEnabled` is the client's "Graphiques" building switch (2026-08-08). It is the
// LAST parameter and defaults to true precisely so that every existing 2- and 3-arg
// call site keeps today's behaviour. Turning it off does not merely hide the chart —
// because `chart` then reads false, rule 1's budget stops deducting ROWS_CHART_BLOCK
// and rule 2 (which exists only to give the chart its own page) is skipped entirely.
// That is what gives the ~14 rows back to the cost lines, so an entry whose costs fit
// on one page collapses from two pages to one, as promised to the client.
export function splitObjectDetail(d, acomptesEnabled = true, variant = 'object', graphEnabled = true) {
  const costLines = d.cost_lines || [];
  const n = costLines.length;
  const chart = graphEnabled && hasChartData(d);
  // Pass the entry's real Consommations row count so a 6-line (or switched-down)
  // building is budgeted for what it actually prints.
  //
  // `|| LEGACY_CONSO_LINES` was WRONG here: 0 is falsy, so a building with every line
  // switched off still reserved a 5-row block for a table that renders nothing. An
  // ABSENT array (legacy payload) genuinely means five; an EMPTY one means zero, and
  // the two must be distinguished rather than collapsed by a falsy test.
  const consoLineCount = Array.isArray(d.consommations)
    ? d.consommations.length
    : LEGACY_CONSO_LINES;
  // Nothing to print ⇒ the block occupies no rows and is not rendered at all.
  const hasConsoRows = consoLineCount > 0;
  const budget = (blocks) => costRowBudget({
    ...blocks,
    // A block that will not render must not be reserved for, or every entry loses
    // rows to whitespace.
    conso: blocks.conso && hasConsoRows,
    acomptes: acomptesEnabled,
    variant,
    consoLines: consoLineCount,
  });
  const noTail = budget({});

  // 1. Everything together.
  if (n <= budget({ totals: true, conso: true, chart })) {
    return [{ lines: costLines, showTotals: true, showConso: true, showChart: chart }];
  }
  // 2. Break after the Consommations — only the graph moves. (Étang A lands here:
  //    44 accounts ≤ 44, so the whole cost list stays intact on page 1.)
  if (chart && n <= budget({ totals: true, conso: true })) {
    return [
      { lines: costLines, showTotals: true, showConso: true, showChart: false },
      { lines: [], showTotals: false, showConso: false, showChart: true },
    ];
  }
  // 3. Break after the Total CHF — Consommations and the graph move together.
  if (n <= budget({ totals: true })) {
    return [
      { lines: costLines, showTotals: true, showConso: false, showChart: false },
      { lines: [], showTotals: false, showConso: true, showChart: chart },
    ];
  }
  // 4. The cost list genuinely doesn't fit one page. Fill from the front at full
  //    density; the final page carries whatever tail still fits beside its rows.
  const lastBudget = budget({ totals: true, conso: true, chart });
  const chunks = [];
  let i = 0;
  // Guard: if the tail leaves no room for cost rows, give it a page of its own
  // rather than looping forever.
  if (lastBudget < 1) {
    for (; i < n; i += noTail) {
      chunks.push({ lines: costLines.slice(i, i + noTail), showTotals: false, showConso: false, showChart: false });
    }
    chunks.push({ lines: [], showTotals: true, showConso: true, showChart: chart });
    return chunks;
  }
  while (n - i > lastBudget) {
    chunks.push({ lines: costLines.slice(i, i + noTail), showTotals: false, showConso: false, showChart: false });
    i += noTail;
  }
  chunks.push({ lines: costLines.slice(i), showTotals: true, showConso: true, showChart: chart });
  return chunks;
}

// Number of landscape pages this object occupies. Preview.js imports this so the
// page-number + bookmark math never drifts from the actual render.
export function objectDetailPageCount(d, acomptesEnabled = true, variant = 'object', graphEnabled = true) {
  return splitObjectDetail(d, acomptesEnabled, variant, graphEnabled).length;
}

const ObjectDetail = ({
  data,
  capped = false,
  omittedCount = 0,
  omittedPages = 0,
  capThreshold = 0,
  building,
  periodLabel,
  periodYears,
  whiteLabelling,
  logoUrl,
  acomptesEnabled = true,
  basePageNumber = 1,
  grandTotalPages = 1,
  variant = 'object',
  // Client's "Graphiques" building switch (2026-08-08). Defaults to true so a caller
  // that does not pass it renders exactly as before.
  graphEnabled = true,
}) => {
  const details = data?.details || [];
  const v = VARIANTS[variant] || VARIANTS.object;

  // Large-building guard: past the cap the per-object detail pages are omitted to
  // keep the browser-side PDF export from exhausting memory. Render a single note
  // page in their place (all summary sections are still produced). Kept in sync
  // with Preview.js (DETAIL_PAGE_BUDGET → objectDetailPages = 1 when capped).
  if (capped) {
    return (
      <A4PageFrame
        building={building}
        periodLabel={periodLabel}
        sectionTitle={v.title}
        whiteLabelling={whiteLabelling}
        logoUrl={logoUrl}
        pageNumber={basePageNumber}
        totalPages={grandTotalPages}
        resultsPage
        hidePageNumber
      >
        <div style={{ marginTop: 24, fontSize: 11, lineHeight: "18px", color: "#00033F", maxWidth: 620 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            {v.notIncluded}
          </div>
          <div>
            Cet immeuble comporte {fmtNum(omittedCount, { decimals: 0 })} {v.noun}, soit{" "}
            {fmtNum(omittedPages, { decimals: 0 })} pages de détail. Pour préserver la
            lisibilité et la performance de génération du rapport, les pages
            « {v.title} » ne sont pas incluses au-delà de{" "}
            {fmtNum(capThreshold, { decimals: 0 })} pages. Toutes les sections de synthèse
            (Modules, Résultats par module, par objet et par locataire) restent complètes.
          </div>
        </div>
      </A4PageFrame>
    );
  }

  if (details.length === 0) return null;

  const yearLabel = periodYears?.current ?? "";
  let pageNo = basePageNumber;
  const frames = [];

  details.forEach((d, idx) => {
    const chunks = splitObjectDetail(d, acomptesEnabled, variant, graphEnabled);
    chunks.forEach((chunk, chunkIdx) => {
      // Client request: no "(x/N)" running counter in the title and no bottom page
      // number — this section is delivered per-tenant. Continuation pages get a
      // "(suite)" hint so a multi-page entry reads clearly.
      const label = chunkIdx === 0 ? v.title : `${v.title} (suite)`;

      frames.push(
        <A4PageFrame
          // The entry index is part of the key: an objet code is not unique — a lease
          // imported on two modules has a page per part (Confédération 06862.20.2100.07),
          // and a code-only key gave both the same key, so React could drop one.
          key={`${d.objet_code || "entry"}-${idx}-${chunkIdx}`}
          building={building}
          periodLabel={periodLabel}
          sectionTitle={label}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={pageNo++}
          totalPages={grandTotalPages}
          resultsPage
          hidePageNumber
        >
          {/* Header block repeats on every page of the entry.
              - objet: objet_code carries the real code (Primerose) or the full raw
                reference (Étang B/Sévelin). Five rows — UNCHANGED from Phase 3.
              - module: three rows, matching the client's mock-up tab "90010"
                (Module / Période / Surface m2 — no Objet, no Locataire).
              Période on a module page is the group's real occupation span (widest
              range across its member leases), which the backend already resolves. */}
          <div style={{ marginBottom: 10 }}>
            {variant === 'object' && (
              <>
                <Info label="Objet" value={d.objet_code || d.reference} />
                <Info label="Locataire" value={d.locataire || "Vacant"} />
                <Info label="Module" value={d.module} />
              </>
            )}
            {variant === 'module' && <Info label="Module" value={d.module} />}
            {variant === 'tenant' && (
              <>
                <Info label="Locataire" value={d.locataire || "Vacant"} />
                <Info label="Objets" value={listOrCount(d.objets, d.member_count, 'objets')} />
                <Info label="Modules" value={listOrCount(d.modules, null, 'modules')} />
              </>
            )}
            <Info label="Période" value={`${fmtDate(d.fee_start)} - ${fmtDate(d.fee_end)}`} />
            <Info label="Surface m²" value={fmtNum(d.surface_m2)} />
          </div>

          {/* Cost-line breakdown — width:100% so it reflows to the portrait content
              width and never overflows the capture box. This chunk's cost lines.
              SKIPPED ENTIRELY on a page that carries neither cost rows nor the
              totals: the block-aware split (rules 2 and 3) can hand a page just the
              Consommations and/or the graph, and rendering the table there left a
              dangling "Compte | Désignation | …" column header above nothing. */}
          {(chunk.lines.length > 0 || chunk.showTotals) && (
          <table style={detailTable}>
            <DetailCols />
            <thead>
              <tr style={{ borderBottom: "1px solid #00033F" }}>
                <th style={th}>Compte</th>
                <th style={th}>Désignation</th>
                <th style={thNum}>{yearLabel} HT</th>
                <th style={thNum}>CHF/m2</th>
                <th style={thNum}>{yearLabel} TTC</th>
                <th style={thNum}>Total HT</th>
                <th style={thNum}>Part %</th>
              </tr>
            </thead>
            <tbody>
              {/* Client (round 5): accounts the object doesn't bear show "-" (not
                  0.00). Plain fmtNum/fmtPct render 0 and null alike as "-". */}
              {chunk.lines.map((c, i) => (
                <tr key={i}>
                  <td style={td}>{c.cost}</td>
                  <td style={tdEllipsis}>{c.designation || ""}</td>
                  <td style={tdNum}>{fmtNum(c.ht)}</td>
                  <td style={tdNum}>{fmtNum(c.chf_m2)}</td>
                  <td style={tdNum}>{fmtNum(c.ttc)}</td>
                  <td style={tdNum}>{fmtNum(c.total_ht)}</td>
                  <td style={tdNum}>{fmtPct(c.part_pct)}</td>
                </tr>
              ))}
              {/* Totals render only on the last chunk of the object. */}
              {chunk.showTotals && (
                <>
                  <tr style={{ borderTop: "1px solid #00033F", fontWeight: 700 }}>
                    <td style={td} colSpan={2}>Total CHF</td>
                    <td style={tdNum}>{fmtNum(d.total?.ht)}</td>
                    <td style={tdNum}>{fmtNum(d.total?.chf_m2)}</td>
                    <td style={tdNum}>{fmtNum(d.total?.ttc)}</td>
                    {/* Total HT column total (building grand total) + aggregate Part %
                        (object HT / building HT) — client request. */}
                    <td style={tdNum}>{fmtNum(d.total?.total_ht_all)}</td>
                    <td style={tdNum}>{fmtPct(d.total?.part_pct)}</td>
                  </tr>
                  {/* Acomptes/Solde lines hidden when the per-period Acomptes
                      toggle is OFF (presentation only). Pagination reserves a
                      conservative tail budget, so removing these never clips. */}
                  {acomptesEnabled && (
                    <>
                      <tr>
                        <td style={td} colSpan={4}>Acomptes</td>
                        <td style={tdNum}>{fmtNum(d.acomptes)}</td>
                        <td style={tdNum} colSpan={2} />
                      </tr>
                      <tr style={{ fontWeight: 700 }}>
                        <td style={td} colSpan={4}>Solde</td>
                        <td style={tdNum}>{fmtNum(d.solde)}</td>
                        <td style={tdNum} colSpan={2} />
                      </tr>
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
          )}

          {/* Consommations — rendered ONLY on the entry's last page so the block is never
              split/clipped. Missing meters render "-" (fmtNum(null)).
              Row count comes from the building's configuration; `hasConso` suppresses
              the whole table — header included — when there is nothing to show.
              Client 2026-09-04: "I disable all of the Consommations on project
              Confederation Centre but it still appears on final repport … Can you set
              to hide the header when all consumptions are disable?" The backend was
              already returning zero rows; the header was rendered unconditionally, so
              the section appeared as a lone column header under Total CHF. */}
          {chunk.showConso && (d.consommations || []).length > 0 && (
            // Client 2026-10-15: "alligne Consommations and Total Immeuble similar to
            // costs above position". Same <DetailCols /> grid as the cost table, so
            // every figure sits under its cost counterpart:
            //   Consommations → Compte+Désignation · <year> → <year> HT ·
            //   CHF/unité → CHF/m2 · (empty) → <year> TTC ·
            //   Total Immeuble → Total HT · Part % → Part %
            <table style={detailTable}>
              <DetailCols />
              <thead>
                <tr style={{ borderBottom: "1px solid #00033F" }}>
                  <th style={th} colSpan={2}>Consommations</th>
                  <th style={thNum}>{yearLabel}</th>
                  <th style={thNum}>CHF/unité</th>
                  <th style={thNum} />
                  <th style={thNum}>Total Immeuble</th>
                  <th style={thNum}>Part %</th>
                </tr>
              </thead>
              <tbody>
                {(d.consommations || []).map((c, i) => (
                  <tr key={i}>
                    <td style={tdEllipsis} colSpan={2}>{c.energy}{c.unit ? ` ${c.unit}` : ""}</td>
                    <td style={tdNum}>{fmtNum(c.object_value)}</td>
                    <td style={tdNum}>{fmtNum(c.chf_unite, { decimals: 3 })}</td>
                    <td style={tdNum} />
                    <td style={tdNum}>{fmtNum(c.total_immeuble)}</td>
                    <td style={tdNum}>{fmtPct(c.part_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Objet/Module/Locataire vs Immeuble chart — restored 31.07.2026 at the
              client's request, INLINE under Consommations on the entry's last page
              (never on a page of its own, which is why it was removed before).
              splitObjectDetail() reserved CHART_H + CHART_MARGIN for it via the
              same hasChartData() guard used here, so the reservation and the
              render can never disagree. */}
          {chunk.showChart && (
            <div style={chartBox}>
              <ObjectVsImmeubleChart
                data={toChartData(d)}
                width={720}
                height={CHART_H}
                objectLabel={v.chartLabel}
              />
            </div>
          )}
        </A4PageFrame>
      );
    });
  });

  return <>{frames}</>;
};

// One header line. The value is PINNED TO A SINGLE LINE (nowrap + ellipsis): the
// client asked for the Objets/Modules lists to "keep one line that will have the
// detail of 90% of cases". Beyond being what he wants, it is what keeps the header
// height fixed at exactly 15 px per row — a wrapping value would silently steal a
// cost row from the pagination budget and clip the bottom of the page.
function Info({ label, value }) {
  return (
    <div style={{ display: "flex", fontSize: 10, lineHeight: "15px" }}>
      <span style={{ width: 90, fontWeight: 600, color: "#00033F", flex: "0 0 90px" }}>{label}</span>
      <span style={{ color: "#00033F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {value}
      </span>
    </div>
  );
}

// Chart wrapper. The client's 2026-2024 reference PDF frames the graph in a light
// rounded box with the legend inside it, so the restored inline version keeps that
// look ("introduce graphs like the period 2024"). The 1 px border + 6 px padding are
// inside the height the pagination budget already reserves — verified by real-browser
// measurement (overflowY = 0 on the tightest 30-account single page).
const chartBox = {
  marginTop: CHART_MARGIN,
  border: "1px solid rgba(0,3,63,0.15)",
  borderRadius: 4,
  padding: "6px 8px",
};

// Shared column grid for the cost table AND the Consommations table. Client
// 2026-10-15 asked for Consommations to line up under the costs; with automatic
// layout each table sized its own columns from its own content, so the two never
// matched (only Part %, flush right, happened to). tableLayout:"fixed" + one
// <colgroup> makes both grids identical. Désignation has no width and takes the
// rest of the 730 px content band (~218 px — about what auto layout gave it).
// Widths fit the widest real figures across every building, bold Total row
// included: "3'902'869.15" (Espace Tourbillon, locataire) in the HT/TTC columns
// and the "Total Immeuble" header in the Total HT column; CHF/m2 is 88 px for the
// bold "634'377.02" (Confédération Centre, module Total row).
const DETAIL_COL_WIDTHS = [64, null, 96, 88, 96, 104, 64];
const DetailCols = () => (
  <colgroup>
    {DETAIL_COL_WIDTHS.map((w, i) => <col key={i} style={w ? { width: w } : undefined} />)}
  </colgroup>
);
const detailTable = { width: "100%", borderCollapse: "collapse", marginBottom: 12, tableLayout: "fixed" };

// nowrap: under the fixed layout a header can no longer widen its column, so it
// must never wrap either — a two-line header would steal height from the
// calibrated pagination budget.
const th = { padding: "2px 6px", fontWeight: 600, textAlign: "left", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const thNum = { ...th, textAlign: "right" };
// Client feedback 2026-07-21: reduce space between lines so Étang A (41 cost
// accounts) fits its Résultat détaillé on a single page. Row height math:
//   padding 1 + lineHeight 12 + padding 1 + border 1 = 15 px per row.
// Combined with the updated cost-row budgets in the pagination helpers above
// (COST_ROWS_ON_LAST_PAGE_ACOMPTES_* were recomputed against 15 px rows),
// 41 costs + 5-row Consommations + Total/Acomptes/Solde fit ONE portrait page
// even with the Acomptes toggle ON. Padding 1 px is tight but stays legible at
// fontSize 9.5 with the 12 px lineHeight cushion.
const td = { padding: "1px 6px", fontSize: 9.5, lineHeight: "12px", borderBottom: "1px solid rgba(0,3,63,0.05)" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
// Désignation / energy-label cell: ellipsize long names so they never widen the
// column (landscape has ample width, but this keeps rows tidy).
const tdEllipsis = { ...td, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 };

export default ObjectDetail;
